import { useEffect, useRef, useState } from "react";
import { X, Pencil, Check } from "lucide-react";
import { Button } from "@component/button";
import { parseCsv, listAllPeople } from "@service/training";
import type { EmployeeRow } from "@type/training";

// Module-level cache for the autocomplete people list. listAllPeople() is an
// expensive N+1 fan-out (GET /training/campaigns + /campaigns/:id per campaign),
// so cache the mapped result across ALL uploader mounts — reopening the dialog,
// or mounting a second uploader, reuses it instead of re-running the fan-out.
// pastPeoplePromise de-dupes concurrent in-flight loads; it's cleared on settle
// so a failed load can be retried by a later mount rather than caching the error.
// Longer-term: replace with a batch endpoint (see
// docs/TRAINING_CAMPAIGN_SCENARIO_BE_HANDOFF_2026-06-09.md).
let pastPeopleCache: EmployeeRow[] | null = null;
let pastPeoplePromise: Promise<EmployeeRow[]> | null = null;

function loadPastPeople(): Promise<EmployeeRow[]> {
    if (pastPeopleCache) return Promise.resolve(pastPeopleCache);
    if (!pastPeoplePromise) {
        pastPeoplePromise = listAllPeople()
            .then((people) => {
                pastPeopleCache = people.map((p) => ({
                    name: p.name,
                    email: p.email,
                    role: p.role ?? undefined,
                    department: p.department ?? undefined,
                }));
                return pastPeopleCache;
            })
            .finally(() => {
                pastPeoplePromise = null;
            });
    }
    return pastPeoplePromise;
}

interface Props {
    onParsed: (rows: EmployeeRow[]) => void;
    showManual?: boolean;
    initialRows?: EmployeeRow[];
}

export function TrainingCsvUploader({
    onParsed,
    showManual = false,
    initialRows = [],
}: Props) {
    const [rows, setRows] = useState<EmployeeRow[]>(initialRows);
    const [errors, setErrors] = useState<string[]>([]);
    const [fileName, setFileName] = useState<string>("");

    const [manualName, setManualName] = useState("");
    const [manualEmail, setManualEmail] = useState("");
    const [manualRole, setManualRole] = useState("");
    const [manualDept, setManualDept] = useState("");
    const [manualError, setManualError] = useState("");

    const [editingIdx, setEditingIdx] = useState<number | null>(null);
    const [editDraft, setEditDraft] = useState<EmployeeRow>({ name: "", email: "" });

    // Autocomplete: people added to any past campaign, used to suggest
    // name/email/role/department while typing in the manual entry fields.
    const [pastPeople, setPastPeople] = useState<EmployeeRow[]>([]);
    const [focusedField, setFocusedField] = useState<"name" | "email" | null>(null);
    // Attempt the (expensive) people list at most once per mount. Non-mock,
    // listAllPeople() fans out to GET /training/campaigns + /campaigns/:id per
    // campaign, so fetching eagerly on every dialog open is an O(N) burst. Gate
    // it behind "user has started typing" + this once-flag instead.
    //
    // This MUST be a ref, not state: flipping it inside the effect would
    // re-render and re-run the effect, firing its cleanup and cancelling the
    // in-flight fetch before setPastPeople ever lands — the suggestions would
    // never populate. A ref mutates without re-rendering.
    const hasAttemptedPastPeopleRef = useRef(false);

    // Query in the focused field (lowercased for case-insensitive matching).
    // Drives both the autocomplete filter below and the fetch gate.
    const suggestQuery = (
        focusedField === "name"
            ? manualName
            : focusedField === "email"
              ? manualEmail
              : ""
    )
        .trim()
        .toLowerCase();

    useEffect(() => {
        if (!showManual || hasAttemptedPastPeopleRef.current) return;
        // Defer the fan-out until the user actually types — opening the dialog
        // alone must never trigger it.
        if (suggestQuery.length < 2) return;
        // Mark attempted before the request resolves, so a failed fetch still
        // counts as the one allowed attempt and later keystrokes don't re-fire
        // the fan-out. A ref mutation here does NOT re-render/cancel the fetch.
        hasAttemptedPastPeopleRef.current = true;
        // No cleanup-based cancellation: loadPastPeople() is cached + idempotent
        // (no stale-result race to guard), and a late resolve after unmount is a
        // harmless no-op. Cancelling on a suggestQuery change would instead drop
        // the very result we just paid the fan-out for.
        loadPastPeople()
            .then((people) => setPastPeople(people))
            .catch(() => {
                /* suggestions are a non-critical enhancement — ignore failures */
            });
    }, [showManual, suggestQuery]);

    const updateRows = (next: EmployeeRow[]) => {
        setRows(next);
        onParsed(next);
    };

    const handleFile = async (file: File) => {
        setFileName(file.name);
        setErrors([]);
        const text = await file.text();
        const result = parseCsv(text);
        setErrors(result.errors);
        if (result.errors.length === 0) {
            const existingEmails = new Set(rows.map((r) => r.email.toLowerCase()));
            const incoming = result.rows.filter((r) => !existingEmails.has(r.email.toLowerCase()));
            updateRows([...rows, ...incoming]);
        }
    };

    const loadSample = () => {
        const sample = `name,email,role,department\nAlice Tan,alice.tan@example.com,Support Rep,Customer Service\nBobby Lim,bobby.lim@example.com,Support Rep,Customer Service\nChong Wei,chong.wei@example.com,Senior Support,Operations\nDivya Raj,divya.raj@example.com,Team Lead,Customer Service\nEshan Yap,eshan.yap@example.com,Support Rep,Sales`;
        const result = parseCsv(sample);
        setFileName("sample.csv");
        setErrors(result.errors);
        if (result.errors.length === 0) {
            const existingEmails = new Set(rows.map((r) => r.email.toLowerCase()));
            const incoming = result.rows.filter((r) => !existingEmails.has(r.email.toLowerCase()));
            updateRows([...rows, ...incoming]);
        }
    };

    const handleAddManual = () => {
        setManualError("");
        const trimmedName = manualName.trim();
        const trimmedEmail = manualEmail.trim().toLowerCase();
        if (!trimmedName) {
            setManualError("Name is required.");
            return;
        }
        if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setManualError("A valid email address is required.");
            return;
        }
        if (rows.some((r) => r.email.toLowerCase() === trimmedEmail)) {
            setManualError("This email is already in the list.");
            return;
        }
        const next: EmployeeRow[] = [
            ...rows,
            {
                name: trimmedName,
                email: trimmedEmail,
                role: manualRole.trim() || undefined,
                department: manualDept.trim() || undefined,
            },
        ];
        updateRows(next);
        setManualName("");
        setManualEmail("");
        setManualRole("");
        setManualDept("");
    };

    // "+ Add employee" is enabled only with a full name AND a valid email.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const canAddManual =
        manualName.trim().length > 0 && EMAIL_RE.test(manualEmail.trim());

    const handleRemove = (idx: number) => {
        if (editingIdx === idx) setEditingIdx(null);
        updateRows(rows.filter((_, i) => i !== idx));
    };

    const startEdit = (i: number) => {
        setEditingIdx(i);
        setEditDraft({ ...rows[i] });
    };

    const saveEdit = () => {
        if (editingIdx === null) return;
        const trimmedName = editDraft.name.trim();
        const trimmedEmail = editDraft.email.trim().toLowerCase();
        if (!trimmedName || !trimmedEmail) return;
        const next = rows.map((r, i) =>
            i === editingIdx
                ? {
                      name: trimmedName,
                      email: trimmedEmail,
                      role: editDraft.role?.trim() || undefined,
                      department: editDraft.department?.trim() || undefined,
                  }
                : r
        );
        updateRows(next);
        setEditingIdx(null);
    };

    // Past people matching what's typed in the focused field, excluding anyone
    // already in the list. An empty query (just focused) shows recent people.
    const rowEmails = new Set(rows.map((r) => r.email.toLowerCase()));
    const suggestions = focusedField
        ? pastPeople
              .filter((p) => !rowEmails.has(p.email.toLowerCase()))
              .filter(
                  (p) =>
                      suggestQuery.length === 0 ||
                      p.name.toLowerCase().includes(suggestQuery) ||
                      p.email.toLowerCase().includes(suggestQuery)
              )
              .slice(0, 6)
        : [];

    const applySuggestion = (p: EmployeeRow) => {
        setManualName(p.name);
        setManualEmail(p.email);
        setManualRole(p.role ?? "");
        setManualDept(p.department ?? "");
        setManualError("");
        setFocusedField(null);
    };

    // Initials for the suggestion avatar — first letter of up to two name parts.
    const suggestionInitials = (name: string) =>
        name
            .trim()
            .split(/\s+/)
            .map((part) => part[0])
            .slice(0, 2)
            .join("")
            .toUpperCase() || "?";

    const renderSuggestions = (field: "name" | "email") =>
        focusedField === field && suggestions.length > 0 ? (
            <ul className="absolute z-20 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                {suggestions.map((p) => (
                    <li key={p.email}>
                        <button
                            type="button"
                            // onMouseDown (not onClick) so the input's onBlur
                            // doesn't close the dropdown before the pick registers.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                applySuggestion(p);
                            }}
                            className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-blue-50"
                        >
                            <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-semibold text-blue-700">
                                {suggestionInitials(p.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-gray-900">
                                    {p.name}
                                </div>
                                <div className="truncate text-xs text-gray-400">{p.email}</div>
                                {(p.role || p.department) && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {p.role && (
                                            <span className="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">
                                                {p.role}
                                            </span>
                                        )}
                                        {p.department && (
                                            <span className="inline-flex items-center rounded-md bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                                                {p.department}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </button>
                    </li>
                ))}
            </ul>
        ) : null;

    return (
        <div>
            {showManual && (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="relative">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={manualName}
                                onChange={(e) => setManualName(e.target.value)}
                                onFocus={() => setFocusedField("name")}
                                onBlur={() =>
                                    setFocusedField((f) => (f === "name" ? null : f))
                                }
                                placeholder="Full name"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {renderSuggestions("name")}
                        </div>
                        <div className="relative">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={manualEmail}
                                onChange={(e) => setManualEmail(e.target.value)}
                                onFocus={() => setFocusedField("email")}
                                onBlur={() =>
                                    setFocusedField((f) => (f === "email" ? null : f))
                                }
                                placeholder="name@company.com"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAddManual();
                                }}
                            />
                            {renderSuggestions("email")}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Role{" "}
                                <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={manualRole}
                                onChange={(e) => setManualRole(e.target.value)}
                                placeholder="e.g. Support Rep"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Department{" "}
                                <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={manualDept}
                                onChange={(e) => setManualDept(e.target.value)}
                                placeholder="e.g. Customer Service"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    {manualError && (
                        <p className="text-xs text-red-600 mb-2">{manualError}</p>
                    )}
                    <div className="flex justify-end mb-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleAddManual}
                            disabled={!canAddManual}
                            className="text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            + Add employee
                        </Button>
                    </div>

                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center">
                            <span className="bg-white px-3 text-xs text-gray-400">
                                or import from a CSV file
                            </span>
                        </div>
                    </div>
                </>
            )}

            <div className="flex items-center gap-3 mb-2">
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleFile(f);
                        }}
                    />
                    <span className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 cursor-pointer">
                        Choose CSV file
                    </span>
                </label>
                <Button variant="ghost" onClick={loadSample} type="button">
                    Use sample data
                </Button>
                {fileName && (
                    <span className="text-sm text-gray-600">{fileName}</span>
                )}
            </div>
            <p className="text-xs text-gray-500 mb-4">
                Required: <code>name</code>, <code>email</code>. Optional:{" "}
                <code>role</code>, <code>department</code>. Max 500 rows.
            </p>

            {errors.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-md p-3 mb-4">
                    <div className="text-sm font-semibold text-red-800 mb-1">
                        {errors.length} error{errors.length > 1 ? "s" : ""} found
                    </div>
                    <ul className="list-disc pl-5 text-xs text-red-700">
                        {errors.slice(0, 10).map((e, i) => (
                            <li key={i}>{e}</li>
                        ))}
                    </ul>
                </div>
            )}

            {rows.length > 0 && (
                <>
                    <div className="flex items-center justify-between mt-5 mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Employees added ({rows.length})
                        </span>
                        <button
                            type="button"
                            onClick={() => updateRows([])}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                            Clear all
                        </button>
                    </div>

                    <div className="border border-gray-200 rounded-md overflow-hidden">
                        <div className="max-h-72 overflow-y-auto overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-200">
                                    <tr>
                                        <th className="text-left px-3 py-2">Name</th>
                                        <th className="text-left px-3 py-2">Email</th>
                                        <th className="text-left px-3 py-2">Role</th>
                                        <th className="text-left px-3 py-2">Department</th>
                                        <th className="w-16 px-3 py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, 50).map((r, i) =>
                                        i === editingIdx ? (
                                            <tr
                                                key={i}
                                                className="border-t border-gray-100 bg-blue-50/40"
                                            >
                                                <td className="px-3 py-1.5">
                                                    <input
                                                        value={editDraft.name}
                                                        onChange={(e) =>
                                                            setEditDraft((d) => ({ ...d, name: e.target.value }))
                                                        }
                                                        className="w-full border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input
                                                        value={editDraft.email}
                                                        onChange={(e) =>
                                                            setEditDraft((d) => ({ ...d, email: e.target.value }))
                                                        }
                                                        className="w-full border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input
                                                        value={editDraft.role ?? ""}
                                                        onChange={(e) =>
                                                            setEditDraft((d) => ({ ...d, role: e.target.value }))
                                                        }
                                                        className="w-full border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input
                                                        value={editDraft.department ?? ""}
                                                        onChange={(e) =>
                                                            setEditDraft((d) => ({ ...d, department: e.target.value }))
                                                        }
                                                        className="w-full border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={saveEdit}
                                                            className="text-green-600 hover:text-green-700"
                                                            title="Save"
                                                        >
                                                            <Check className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingIdx(null)}
                                                            className="text-gray-400 hover:text-gray-600"
                                                            title="Cancel"
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr
                                                key={i}
                                                className="border-t border-gray-100 hover:bg-gray-50 group"
                                            >
                                                <td className="px-3 py-2">{r.name}</td>
                                                <td className="px-3 py-2 text-gray-600">{r.email}</td>
                                                <td className="px-3 py-2 text-gray-600">{r.role || "—"}</td>
                                                <td className="px-3 py-2 text-gray-600">{r.department || "—"}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            type="button"
                                                            onClick={() => startEdit(i)}
                                                            className="text-gray-400 hover:text-blue-600"
                                                            title="Edit"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemove(i)}
                                                            className="text-gray-400 hover:text-red-500"
                                                            title="Remove"
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                            {rows.length > 50 && (
                                <div className="text-xs text-gray-500 text-center py-2 border-t border-gray-100">
                                    ...and {rows.length - 50} more
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
